const DEFAULT_HEARTBEAT_TIMEOUT = 30_000;
const DEFAULT_RECONNECT_INTERVAL = 5_000;
const DEFAULT_PING_RESPONSE_TIMEOUT = 5_000;

export interface WebSocketServiceOptions {
	signal?: AbortSignal;
	heartbeatTimeout?: number;
	reconnectInterval?: number;
	pingResponseTimeout?: number;
}

export class WebSocketService {
	url: string;
	options: WebSocketServiceOptions;
	ws: WebSocket | null;
	reconnectInterval: number;
	listeners: Map<string, Array<(data: any) => void>>;
	isConnected: boolean;
	heartbeat: ReturnType<typeof setTimeout> | null;
	heartbeatTimeout: number;
	pingTimeout: ReturnType<typeof setTimeout> | null;
	pingResponseTimeout: number;
	reconnectTimer: ReturnType<typeof setTimeout> | null;
	signal?: AbortSignal;

	private _shouldReconnect: boolean = true;
	private _onAbort: (() => void) | null = null;

	constructor(url: string, options: WebSocketServiceOptions = {}) {
		this.url = url;
		this.options = options;
		this.ws = null;
		this.reconnectInterval = options.reconnectInterval || DEFAULT_RECONNECT_INTERVAL;
		this.pingResponseTimeout = options.pingResponseTimeout || DEFAULT_PING_RESPONSE_TIMEOUT;
		this.listeners = new Map();
		this.isConnected = false;
		this.heartbeat = null;
		this.pingTimeout = null;
		this.reconnectTimer = null;
		this.heartbeatTimeout = options.heartbeatTimeout || DEFAULT_HEARTBEAT_TIMEOUT;
	}

	connect() {
		if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
			console.warn("WebSocket already open or connecting");
			return;
		}

		this._shouldReconnect = true;
		this._clearReconnectTimer();

		this.ws = new WebSocket(this.url);
		this._bindAbortSignal();

		this.ws.onopen = () => {
			console.debug("WebSocket connected");
			this.isConnected = true;
			this.emit("open");
			this._scheduleHeartbeat();
		};

		this.ws.onmessage = (event) => {
			this._onActivity();
			try {
				const data = JSON.parse(event.data);
				this.emit("message", data);
			} catch (error) {
				console.error("Error parsing WebSocket message:", error);
			}
		};

		this.ws.onerror = (error) => {
			console.error("WebSocket error:", error);
			this.emit("error", error);
		};

		this.ws.onclose = () => {
			this._handleSocketClosed();
		};
	}

	sendPing() {
		this._clearHeartbeat();
		if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify({ type: "ping" }));
			this._clearPingTimeout();
			this.pingTimeout = setTimeout(() => {
				console.warn("No pong received, reconnecting...");
				this._forceReconnect();
			}, this.pingResponseTimeout);
		} else {
			console.warn("WebSocket not connected, cannot send ping");
		}
	}

	send(data: any) {
		if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(data));
		} else {
			console.warn("WebSocket not connected");
		}
	}

	close() {
		this._shouldReconnect = false;
		this._clearReconnectTimer();
		this._unbindAbortSignal();
		this._detachAndCloseSocket();
		this.isConnected = false;
		this.emit("close");
		this.listeners.clear();
		if (wsInstances.get(this.url) === this) wsInstances.delete(this.url);
	}

	/** Subscribe; returns disposer for this callback only. */
	on(event: string, callback: (data: any) => void): () => void {
		if (!this.listeners.has(event)) {
			this.listeners.set(event, []);
		}
		const callbacks = this.listeners.get(event);
		callbacks?.push(callback);
		return () => this.off(event, callback);
	}

	/** Remove one callback, or all listeners for `event` if callback omitted. */
	off(event: string, callback?: (data: any) => void) {
		if (!callback) {
			this.listeners.delete(event);
			return;
		}
		const callbacks = this.listeners.get(event);
		if (!callbacks) return;
		this.listeners.set(
			event,
			callbacks.filter((cb) => cb !== callback),
		);
	}

	emit(event: string, data: any = null) {
		const callbacks = this.listeners.get(event);
		if (callbacks) {
			for (const cb of callbacks) cb(data);
		}
	}

	private _forceReconnect() {
		this._shouldReconnect = true;
		this._clearReconnectTimer();
		this._detachAndCloseSocket();
		this._handleSocketClosed();
	}

	private _handleSocketClosed() {
		this.isConnected = false;
		console.warn("WebSocket closed");
		this.emit("close");
		this._clearHeartbeat();
		this._clearPingTimeout();
		this.ws = null;

		if (this._shouldReconnect) this._scheduleReconnect();
	}

	private _scheduleReconnect() {
		this._clearReconnectTimer();
		console.log(`Reconnecting in ${this.reconnectInterval}ms...`);
		this.emit("reconnect");
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this.connect();
		}, this.reconnectInterval);
	}

	private _detachAndCloseSocket() {
		this._clearHeartbeat();
		this._clearPingTimeout();

		if (!this.ws) return;

		const socket = this.ws;
		this.ws = null;
		socket.onopen = null;
		socket.onmessage = null;
		socket.onerror = null;
		socket.onclose = null;

		if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
			socket.close();
		}
	}

	private _bindAbortSignal() {
		this._unbindAbortSignal();
		const signal = this.options.signal;
		if (!signal) return;

		if (signal.aborted) {
			this.close();
			return;
		}

		this._onAbort = () => this.close();
		signal.addEventListener("abort", this._onAbort, { once: true });
	}

	private _unbindAbortSignal() {
		if (this._onAbort && this.options.signal) this.options.signal.removeEventListener("abort", this._onAbort);
		this._onAbort = null;
	}

	private _onActivity() {
		this._clearPingTimeout();
		if (this.isConnected) this._scheduleHeartbeat();
	}

	private _scheduleHeartbeat() {
		this._clearHeartbeat();
		this.heartbeat = setTimeout(() => this.sendPing(), this.heartbeatTimeout);
	}

	private _clearHeartbeat() {
		if (this.heartbeat != null) {
			clearTimeout(this.heartbeat);
			this.heartbeat = null;
		}
	}

	private _clearPingTimeout() {
		if (this.pingTimeout != null) {
			clearTimeout(this.pingTimeout);
			this.pingTimeout = null;
		}
	}

	private _clearReconnectTimer() {
		if (this.reconnectTimer != null) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}
}

const wsInstances: Map<string, WebSocketService> = new Map();

export function getWebSocketInstance(url: string, options: WebSocketServiceOptions = {}) {
	if (wsInstances.has(url)) {
		const instance = wsInstances.get(url) as WebSocketService;
		const isLive = instance.ws?.readyState === WebSocket.OPEN || instance.ws?.readyState === WebSocket.CONNECTING;
		if (isLive) return instance;

		instance.close();
		wsInstances.delete(url);
	}

	const newInstance = new WebSocketService(url, options);
	wsInstances.set(url, newInstance);
	return newInstance;
}
